import { createContext, useContext } from 'react';

import type CourseMaterials from 'pages/open-course/common/models/courseMaterials';
// eslint-disable-next-line no-restricted-imports
import type ItemMetadata from 'pages/open-course/common/models/itemMetadata';

import { DEFAULT_TIME_COMMITMENT } from 'bundles/adaptive-learning-common/constants';
import type { AdaptiveLearningPlan, LearningBlock, TimeCommitment } from 'bundles/adaptive-learning-common/types';

export type RefetchPlanOptions = {
  showLoadingDialog?: boolean;
};

export type RefetchPlanResult = {
  plan: AdaptiveLearningPlan;
  materials: InstanceType<typeof CourseMaterials>;
};

export type AdaptiveLearningContextType = {
  isEnabled: boolean;
  isCodingPracticeItemEnabled: boolean;
  show: boolean;
  loading: boolean;
  setShow: (show: boolean) => void;
  adaptivePlan?: AdaptiveLearningPlan;
  timeCommitment: TimeCommitment;
  planUpdated: boolean;
  getLearningBlock: (itemId: string) => LearningBlock | undefined;
  regeneratePlan: (timeCommitment: TimeCommitment) => Promise<void>;
  refetchPlan: (options?: RefetchPlanOptions) => Promise<RefetchPlanResult | undefined>;
  isProgressDialogOpen: boolean;
  setIsProgressDialogOpen: (open: boolean) => void;
  isNonPersonalizedLoadingDialogOpen: boolean;
  setIsNonPersonalizedLoadingDialogOpen: (open: boolean) => void;
  getNextItem: () => Promise<ItemMetadata | undefined>;
};

export const AdaptiveLearningContext = createContext<AdaptiveLearningContextType>({
  isEnabled: false,
  isCodingPracticeItemEnabled: false,
  show: false,
  loading: false,
  planUpdated: false,
  setShow: () => undefined,
  timeCommitment: DEFAULT_TIME_COMMITMENT,
  getLearningBlock: () => undefined,
  regeneratePlan: () => Promise.resolve(),
  isProgressDialogOpen: false,
  setIsProgressDialogOpen: () => undefined,
  refetchPlan: () => Promise.resolve(undefined),
  isNonPersonalizedLoadingDialogOpen: false,
  setIsNonPersonalizedLoadingDialogOpen: () => undefined,
  getNextItem: () => Promise.resolve(undefined),
});

export const useAdaptiveLearningContext = () => useContext(AdaptiveLearningContext);
